import {applyMiddleware, compose, createStore} from 'redux';
import thunk from 'redux-thunk';
import rootReducer from 'app/reducers/rootReducer';
import {createInitialSessionData} from 'app/reducers/sessionReducer';
import reduxCookiesMiddleware, {getStateFromCookies} from 'redux-cookies-middleware';
import {createInitialUserData} from 'app/reducers/currentUserReducer';
import {partnerInitData} from 'app/reducers/partnerReducer';
import {windowIsDefined} from 'app/helpers/windowIsDefined';
import {createSsrLocationData} from 'app/reducers/ssrLocationReducer';
import {getElementFromLocalStorage, setElementToLocalStorage} from 'app/helpers/webStorageUtility';
import {featureFlags} from 'app/featureFlags/featureFlags';
import {createInitialEarlyAccessData} from 'app/reducers/earlyAccessReducer';
import is from '@sindresorhus/is';
import SelectedDistrictSlugCookie from 'app/constants/SelectedDistrictSlugCookie';
import {EarlyAccessFeatureStatus} from 'app/earlyAccess/EarlyAccessFeatureStatus';

const defaultCookieExpiryInDays = 365;
const DAY_IN_MS = 86400000;

const earlyAccessFeatureCookie = 'fupaEAF';

const generateEarlyAccessFeaturePaths = earlyAccessFeatures => {
  const paths = {};
  // We only support one early access feature at a time
  const feature = earlyAccessFeatures[0];
  if (feature) {
    paths[`earlyAccessFeatures.${feature.slug}`] = {name: earlyAccessFeatureCookie, expiry: feature.availableUntil};
  }
  return paths;
};

const paths = {
  'session.district.slug': {name: SelectedDistrictSlugCookie},
  'currentUser.ppid': {name: 'fupaPPID', expiry: 180},
  ...generateEarlyAccessFeaturePaths(featureFlags),
};

let composeEnhancers = compose;

if (windowIsDefined && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) {
  composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({trace: true, traceLimit: 25});
}

const getExpiryDate = cookieExpiry => {
  if (cookieExpiry instanceof Date) {
    return cookieExpiry;
  }
  if (is.number(cookieExpiry)) {
    return new Date(Date.now() + cookieExpiry * DAY_IN_MS);
  }

  return new Date(Date.now() + defaultCookieExpiryInDays * DAY_IN_MS);
};

const customSetCookie = (res, name, value, expiry = defaultCookieExpiryInDays) => {
  const cookieToSet = Object.values(paths).find(path => path.name === name);
  const expiryDate = getExpiryDate(cookieToSet?.expiry ?? expiry);

  /**
   * EFFECT: Remove existing and prevent creating cookie "fupaEAF", if it is set to a value other than "disabled"
   * Temporary solution to remove "fupaEAF"-cookie if it is set to a value other than "disabled"
   * This is because we do not want to persist these cookie values ('enabled', 'pending') in the browser for caching reasons
   */
  if (name === earlyAccessFeatureCookie && value !== `"${EarlyAccessFeatureStatus.DISABLED}"`) {
    if (!res) {
      removeCookie(name);
    }
    return;
  }

  if (res) {
    const isSelectedDistrictSlugCookie = name === SelectedDistrictSlugCookie;
    if (isSelectedDistrictSlugCookie) {
      return;
    }
    return res.cookie(name, value, {expires: expiryDate, sameSite: 'lax'});
  } else {
    document.cookie = `${name}=${encodeURIComponent(
      value
    )}; path=/; expires=${expiryDate.toUTCString()}; samesite=lax;`;
  }
};

function getCookieValueByName(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=%22([^;]+)%22'));
  return match ? match[2] : '';
}

function isCookieSet(name) {
  return document.cookie.split(';').some(c => {
    return c.trim().startsWith(name + '=');
  });
}

function removeCookie(name, path) {
  if (isCookieSet(name)) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path ? path : '/'};`;
  }
}

const updateCookieWithDistrictData = slug => {
  customSetCookie(null, SelectedDistrictSlugCookie, `"${slug}"`);
};

const updateDistrictDataWithCookie = (cookieValue, reduxData) => {
  return {...reduxData, session: {...reduxData.session, district: {slug: cookieValue}}, partner: partnerInitData};
};

const isDistrictUrl = (pathname, districtSlug) => {
  const districtUrlRegExp = new RegExp('/(region|test_region)/' + districtSlug);
  return districtUrlRegExp.test(pathname);
};

/**
 * sync cookie with district data (in redux store)
 *
 * 1. cookie is set but no district data
 *    -> set district data from cookie
 * 2. district data is set but no cookie
 *    -> set cookie from district data
 * 3. cookie and district data are set but different
 *   a. district page (district slug in url): district data is set by district slug in url
 *      -> update cookie with district data
 *   b. other pages: district data is set by district of content
 *      -> update district data with cookie
 */
const syncSelectedDistrictSlugCookieWithDistrictData = (reduxData, cookieValue) => {
  const districtSlug = reduxData.session.district?.slug;
  if (cookieValue && !districtSlug) {
    return updateDistrictDataWithCookie(cookieValue, reduxData);
  } else if (districtSlug && !cookieValue) {
    updateCookieWithDistrictData(districtSlug);
  } else if (cookieValue && districtSlug && cookieValue !== districtSlug) {
    if (isDistrictUrl(reduxData.ssrLocation.pathname, districtSlug)) {
      updateCookieWithDistrictData(districtSlug);
    } else {
      return updateDistrictDataWithCookie(cookieValue, reduxData);
    }
  }
  return reduxData;
};

const preprocessReduxData = reduxData => {
  const selectedDistrictCookie = getCookieValueByName(SelectedDistrictSlugCookie);
  return syncSelectedDistrictSlugCookieWithDistrictData(reduxData, selectedDistrictCookie);
};

const preloadStateFromLocalStorage = key => {
  let storageValue = getElementFromLocalStorage(key);
  if (!storageValue) {
    storageValue = getCookieValueByName(key);
    removeCookie(key);
    if (storageValue) setElementToLocalStorage(key, storageValue);
  }
  return storageValue;
};

const makeStore = (url, req, res) => {
  // reads a cookie from the express request object / writes a cookie from the express request object
  let getCookie;
  const options = {
    setCookie: (name, value, expiry) => customSetCookie(res, name, value, expiry),
  };
  let selectedDistrictSlug = null;
  if (req && res) {
    // Apply Cookie Simplification: old cookie (selectedDistrict - district-object), new cookie (selectedDistrictSlug - district-slug)
    // Fill initial session storage with old cookie's district-slug, although old cookie isn't read anymore by redux-cookies-middleware
    // Old Cookie ist deleted in staticRedirects.js
    const selectedDistrict = req.cookies?.['selectedDistrict'];
    if (selectedDistrict) {
      try {
        selectedDistrictSlug = JSON.parse(selectedDistrict).slug;
      } catch (error) {
        selectedDistrictSlug = null;
      }
    }
    getCookie = name => (name in req.cookies ? req.cookies[name] : null);
  }

  let preloadedState = {
    session: createInitialSessionData(selectedDistrictSlug, url),
    currentUser: createInitialUserData(), // need to create initial data, to set state from cookies
    ssrLocation: createSsrLocationData(url),
    earlyAccessFeatures: createInitialEarlyAccessData(),
  };
  if (windowIsDefined && window.REDUX_DATA) {
    preloadedState = preprocessReduxData(window.REDUX_DATA);
    const notificationPermission = preloadStateFromLocalStorage('notification-permission');
    const notificationNoSupportBannerStorage = preloadStateFromLocalStorage('notification-no-support-banner');
    const notificationNoSupportBanner = notificationNoSupportBannerStorage
      ? Number(notificationNoSupportBannerStorage)
      : 0;
    preloadedState.currentUser = {
      ...preloadedState.currentUser,
      notificationPermission,
      notificationNoSupportBanner,
    };
  }
  const preloadedStateFromCookies = getStateFromCookies(preloadedState, paths, getCookie);

  // ⚠️ Do not use the Sentry redux enhancer middleware -> this will create massive performance problems
  //    Before considering again be sure that the redux store is very small!!
  return createStore(
    rootReducer(),
    preloadedStateFromCookies,
    composeEnhancers(applyMiddleware(thunk, reduxCookiesMiddleware(paths, options)))
  );
};

export default makeStore;
